import authService from './authService';
import {
  Invoice,
  InvoiceSearchModel,
  InvoiceCreatePayload,
  InvoiceUpdatePayload,
  InvoiceItemPayload,
  InvoiceEmitResponse,
  SyncInvoiceResponse,
  InvoiceInstallment,
} from '../types/invoice';
import { ReturnInvoiceCreatePayload } from '../types/invoiceReference';
import { PaginatedResponse, ApiResponse } from '../types/common';

// ─── Service ────────────────────────────────────────────────────

class InvoiceService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  private async getBaseUrl(): Promise<string> {
    const { getConfig } = await import('../config');
    return getConfig().api.baseUrl;
  }

  // ─── List ───────────────────────────────────────────────────

  async getInvoices(searchModel: InvoiceSearchModel): Promise<PaginatedResponse<Invoice>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      limit: searchModel.pageSize.toString(),
      ...(searchModel.searchName && { search: searchModel.searchName }),
      ...(searchModel.company_id !== undefined && { company_id: searchModel.company_id.toString() }),
      ...(searchModel.status !== undefined && { status: searchModel.status.toString() }),
      ...(searchModel.model !== undefined && { model: searchModel.model.toString() }),
      ...(searchModel.date_from && { date_from: searchModel.date_from }),
      ...(searchModel.date_to && { date_to: searchModel.date_to }),
      ...(searchModel.customer_name && { customer_name: searchModel.customer_name }),
      ...(searchModel.transaction_type && { transaction_type: searchModel.transaction_type }),
      ...(searchModel.final_customer !== undefined && { final_customer: searchModel.final_customer.toString() }),
      ...(searchModel.orderBy && { orderBy: searchModel.orderBy }),
      ...(searchModel.orderDirection && { orderDirection: searchModel.orderDirection }),
    });
    if (searchModel.status_ids && searchModel.status_ids.length > 0) {
      params.set('status_ids', searchModel.status_ids.join(','));
    }
    if (searchModel.invoice_transaction_type_ids && searchModel.invoice_transaction_type_ids.length > 0) {
      params.set('invoice_transaction_type_ids', searchModel.invoice_transaction_type_ids.join(','));
    }

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/invoices?${params}`);
    if (!response.ok) throw new Error('Failed to fetch invoices');

    const json = await response.json();

    const items: Invoice[] = Array.isArray(json.data) ? json.data
      : Array.isArray(json.data?.data) ? json.data.data
      : [];
    const pg = json.pagination ?? json.data?.pagination ?? {};

    return {
      success: json.success ?? true,
      data: items,
      pagination: {
        page: pg.page ?? searchModel.page,
        limit: pg.page_size ?? pg.pageSize ?? searchModel.pageSize,
        total: pg.total_items ?? pg.total ?? items.length,
        totalPages: pg.total_pages ?? pg.totalPages ?? (Math.ceil((pg.total_items ?? pg.total ?? items.length) / searchModel.pageSize) || 1),
      },
      summary: json.summary ?? undefined,
    };
  }

  // ─── List (Vendas — consumidor final + saída) ──────────────

  /**
   * Lista notas fiscais de venda ao consumidor final.
   * Critério: final_customer=true AND transaction_type='S'
   */
  async getInvoicesSales(searchModel: InvoiceSearchModel): Promise<PaginatedResponse<Invoice>> {
    return this.getInvoices({
      ...searchModel,
      transaction_type: 'S',
      final_customer: true,
    });
  }

  // ─── List (Suprimentos — demais operações) ─────────────────

  /**
   * Lista notas fiscais do módulo de suprimentos.
   * Critério: (transaction_type IN ('E','S')) AND (final_customer=false OR (final_customer=true AND transaction_type='E'))
   * Utiliza endpoint filtrado pelo backend com scope=supply
   */
  async getInvoicesSupply(searchModel: InvoiceSearchModel): Promise<PaginatedResponse<Invoice>> {
    return this.getInvoices({
      ...searchModel,
      transaction_type: 'supply',
    });
  }

  // ─── Get by ID ──────────────────────────────────────────────

  async getInvoiceById(id: number): Promise<Invoice> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/invoices/${id}`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Nota fiscal não encontrada');
      throw new Error('Failed to fetch invoice');
    }
    const data: ApiResponse<Invoice> = await response.json();
    return data.data!;
  }

  /**
   * Lista as parcelas (formas de pagamento) da nota fiscal.
   * GET /api/invoices/:id/installments
   */
  async getInvoiceInstallments(id: number): Promise<InvoiceInstallment[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/invoices/${id}/installments`);
    if (!response.ok) {
      throw new Error('Failed to fetch invoice installments');
    }
    const data: ApiResponse<InvoiceInstallment[]> = await response.json();
    return data.data ?? [];
  }

  // ─── Emitir NF-e (direto na SEFAZ) ──────────────────────────

  /**
   * Emite uma nova nota fiscal direto na SEFAZ. A nota autorizada é gravada em
   * [Financial].[Invoice] pelo próprio endpoint (o integrador Bling foi descontinuado).
   * O caminho da rota (create-bling) é mantido por compatibilidade.
   * POST /api/invoices/create-bling
   */
  async createInvoiceBling(payload: InvoiceCreatePayload): Promise<InvoiceEmitResponse> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.post('/invoices/create-bling', payload);

    const data: InvoiceEmitResponse = await res.json();

    // Mesmo em HTTP 4xx/5xx, o backend retorna o corpo com success=false
    return data;
  }

  // ─── Update ─────────────────────────────────────────────────

  async updateInvoice(id: number, payload: InvoiceUpdatePayload): Promise<Invoice> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/invoices/${id}`, payload);
    if (!response.ok) throw new Error('Failed to update invoice');
    const data: ApiResponse<Invoice> = await response.json();
    return data.data!;
  }

  // ─── Cancelar Nota (evento 110111, SEFAZ direto) ──────────

  async cancelInvoice(id: number, justificativa: string): Promise<InvoiceEmitResponse> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.post(`/invoices/${id}/cancel`, { justificativa });
    const data: InvoiceEmitResponse = await res.json();
    return data;
  }

  // ─── Check SEFAZ Status (Consultar Situação) ──────────────

  async checkSefazStatus(id: number): Promise<Invoice> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/invoices/${id}/sefaz-status`);
    if (!response.ok) throw new Error('Erro ao consultar situação no SEFAZ');
    const data: ApiResponse<Invoice> = await response.json();
    return data.data!;
  }

  // ─── Consultar situação na SEFAZ ───────────────────────────

  /**
   * Consulta sob demanda a situação da NF-e diretamente na SEFAZ pela chave de acesso
   * (NfeConsultaProtocolo4) e atualiza o status local (Autorizada/Cancelada/Denegada).
   * Substitui a antiga sincronização via integrador Bling.
   * POST /api/invoices/{id}/sync
   */
  async syncInvoice(id: number, companyId: number): Promise<SyncInvoiceResponse> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.post(`/invoices/${id}/sync`, { company_id: companyId });
    const data: SyncInvoiceResponse = await res.json();
    return data;
  }

  // ─── Download XML ─────────────────────────────────────────

  async downloadXml(id: number): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/invoices/${id}/xml`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `Erro ao baixar XML (${response.status})`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? `nfe-${id}.xml`;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  // ─── Print PDF (DANFE) ────────────────────────────────────

  async downloadPdf(id: number): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/invoices/${id}/pdf`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `Erro ao gerar PDF (${response.status})`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? `danfe-${id}.pdf`;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  // ─── Shipping Label ─────────────────────────────────────────

  async downloadShippingLabel(id: number): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/invoices/${id}/shipping-labels`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `Erro ao gerar etiqueta de envio (${response.status})`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? `etiqueta-nf-${id}.pdf`;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  // ─── Export XMLs (ZIP) ──────────────────────────────────────

  async exportXmlZip(
    dateFrom: string,
    dateTo: string,
    options?: {
      transaction_type?: string;
      final_customer?: boolean;
      status_ids?: number[];
      invoice_transaction_type_ids?: number[];
    },
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
    if (options?.transaction_type) {
      params.set('transaction_type', options.transaction_type);
    }
    if (options?.final_customer !== undefined) {
      params.set('final_customer', options.final_customer.toString());
    }
    if (options?.status_ids && options.status_ids.length > 0) {
      params.set('status_ids', options.status_ids.join(','));
    }
    if (options?.invoice_transaction_type_ids && options.invoice_transaction_type_ids.length > 0) {
      params.set('invoice_transaction_type_ids', options.invoice_transaction_type_ids.join(','));
    }

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/invoices/xml-export?${params}`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `Erro ao exportar XMLs (${response.status})`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    // Build descriptive filename with filter params
    let fallbackFilename = `notas-fiscais-${dateFrom}-a-${dateTo}`;
    if (options?.status_ids && options.status_ids.length > 0) {
      fallbackFilename += `-status-${options.status_ids.join('_')}`;
    }
    if (options?.invoice_transaction_type_ids && options.invoice_transaction_type_ids.length > 0) {
      fallbackFilename += `-nat-${options.invoice_transaction_type_ids.join('_')}`;
    }
    fallbackFilename += '.zip';
    const filename = filenameMatch?.[1] ?? fallbackFilename;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  // ─── Item CRUD ──────────────────────────────────────────────

  async addItem(invoiceId: number, payload: InvoiceItemPayload): Promise<Invoice> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.post(`/invoices/${invoiceId}/items`, payload);
    if (!res.ok) throw new Error('Erro ao adicionar item');
    const data: ApiResponse<Invoice> = await res.json();
    return data.data!;
  }

  async updateItem(invoiceId: number, itemId: number, payload: InvoiceItemPayload): Promise<Invoice> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.put(`/invoices/${invoiceId}/items/${itemId}`, payload);
    if (!res.ok) throw new Error('Erro ao atualizar item');
    const data: ApiResponse<Invoice> = await res.json();
    return data.data!;
  }

  async deleteItem(invoiceId: number, itemId: number): Promise<Invoice> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.delete(`/invoices/${invoiceId}/items/${itemId}`);
    if (!res.ok) throw new Error('Erro ao excluir item');
    const data: ApiResponse<Invoice> = await res.json();
    return data.data!;
  }

  // ─── Lookup by number (quick navigation) ─────────────────────

  /**
   * Busca o InvoiceId pelo número ou chave de acesso da nota fiscal.
   * Endpoint: GET /api/invoices/lookup?number=<number>[&company_id=<id>]
   *           GET /api/invoices/lookup?access_key=<44 digits>
   * Retorna 404 se não encontrada, 409 se houver múltiplas notas com o mesmo
   * número e a empresa não for especificada.
   */
  async lookupInvoice(numberOrKey: string, companyId?: number): Promise<number> {
    const apiClient = await this.getApiClient();
    const trimmed = numberOrKey.trim();
    const params = new URLSearchParams();

    // Se for 44 dígitos numéricos, busca por chave de acesso
    if (/^\d{44}$/.test(trimmed)) {
      params.set('access_key', trimmed);
    } else {
      params.set('number', trimmed);
      if (companyId !== undefined) params.set('company_id', companyId.toString());
    }

    const response = await apiClient.get(`/invoices/lookup?${params}`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Nota fiscal não encontrada');
      if (response.status === 409) throw new Error('Múltiplas notas encontradas — selecione a empresa');
      throw new Error('Erro ao buscar nota fiscal');
    }
    const data: ApiResponse<{ invoice_id: number }> = await response.json();
    return data.data!.invoice_id;
  }

  // ─── Search invoices for return (devolução) ─────────────────

  /**
   * Busca notas fiscais candidatas para devolução.
   * Filtra por empresa, notas não canceladas, com tipo de transação de saída.
   * GET /api/invoices?company_id=X&transaction_type=S&status_not=2&customer_name=Y&number=Z&search=Z
   */
  async searchInvoicesForReturn(params: {
    company_id: number;
    customer_name?: string;
    number?: string;
    page?: number;
    pageSize?: number;
  }): Promise<PaginatedResponse<Invoice>> {
    const searchParams = new URLSearchParams({
      page: (params.page ?? 1).toString(),
      limit: (params.pageSize ?? 20).toString(),
      company_id: params.company_id.toString(),
      transaction_type: 'S',
      status_not: '2', // exclude cancelled
    });
    if (params.customer_name) searchParams.set('customer_name', params.customer_name);
    if (params.number) {
      searchParams.set('number', params.number);
      // Also pass as 'search' param since backend may use generic search field
      searchParams.set('search', params.number);
    }

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/invoices?${searchParams}`);
    if (!response.ok) throw new Error('Erro ao buscar notas fiscais para devolução');

    const json = await response.json();
    const items: Invoice[] = Array.isArray(json.data) ? json.data : [];
    const pg = json.pagination ?? {};

    return {
      success: json.success ?? true,
      data: items,
      pagination: {
        page: pg.page ?? (params.page ?? 1),
        limit: pg.page_size ?? (params.pageSize ?? 20),
        total: pg.total_items ?? pg.total ?? items.length,
        totalPages: pg.total_pages ?? 1,
      },
    };
  }

  // ─── Create Return Invoice (Devolução) ──────────────────────

  /**
   * Cria uma nota fiscal de devolução via integração Bling/gRPC.
   * O backend deve:
   *   1. Criar a NF no Bling com finalidade=4 (Devolução) e tipo=0 (Entrada)
   *   2. Preencher os campos documento_referenciado e documentos_referenciados no gRPC
   *   3. Preencher documento_referenciado em cada item do gRPC
   *   4. Gravar os registros em [financial].[InvoiceReference] após o webhook
   *
   * POST /api/invoices/create-return
   */
  async createReturnInvoice(payload: ReturnInvoiceCreatePayload): Promise<InvoiceEmitResponse> {
    const apiClient = await this.getApiClient();
    const res = await apiClient.post('/invoices/create-return', payload);
    const data: InvoiceEmitResponse = await res.json();
    return data;
  }

  // ─── Unlink Order (desvincular pedido de nota rejeitada) ───

  /**
   * Remove a vinculação entre uma nota fiscal rejeitada e o pedido de venda.
   * Isso permite ao usuário gerar uma nova nota para o mesmo pedido.
   * POST /api/v1/invoices/:id/unlink-order
   */
  async unlinkOrder(invoiceId: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/invoices/${invoiceId}/unlink-order`);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || body.error || 'Erro ao desvincular pedido da nota fiscal');
    }
  }
}

// Singleton instance
export const invoiceService = new InvoiceService();
export default invoiceService;
